import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { APPROVAL_ISSUE_LOG, isApprovalIssue, mergeQueueHandles, type ActorDTO, type GitSnapshotDTO, type MessageDTO, type ProvenanceDTO, type RouteSuggestion, type RunDTO, type TaskDetail, type TaskDTO, type ThreadDTO } from "@roomate/core";
import { ProjectArchivedNotice } from "../components/ProjectArchivedNotice.tsx";
import { useAbandonMerge, useActors, useAssign, useChiefActions, useClaim, useGitSnapshot, useMe, useMessages, useProject, useProjectArchived, useRoute, useRuns, useTask, useThreads, useTransition } from "../api.ts";
import { builderOptions, historyName, reviewerOptions } from "../roster.ts";
import { Action, Avatar, Bento, Check, Disclosure, Page, PageHeader, Pill, Ring, Tile } from "../calm.tsx";
import { AbandonBlastRadius, AbandonedBadge, AbandonmentLine, AbandonReasonInput, abandonmentFor, canAbandonMerge, dependentsForAbandon, type AbandonmentView, type AffectedDependent } from "../abandon-merge.tsx";
import { parkNote, type ParkNote } from "../park-note.ts";
import { approachCard, type ApproachCard } from "../approach.ts";
import { RunRuntime } from "../components/RunRuntime.tsx";
import { TaskChanges } from "../components/TaskChanges.tsx";
import { DecisionCrumb } from "../components/DecisionCrumb.tsx";
import { RUN_STATUS_LABEL, money } from "./Run.tsx";
import { ActorName, cx, describeEvent, ErrorNote, formatAgo, formatMin, inputCls, Markdown, STAGE_LABEL, useNames } from "../ui.tsx";
import { byModelLine, trackLine, visibleTrack } from "../lib/routing-track.ts";
import { Dialog, Field } from "./Project.tsx";
import { chiefActionsForTask, chiefTimelineLine } from "../lib/chief.ts";

/**
 * TODO(#140): mirrors the `TaskDetail.noAgentFit` field apps/server will add to the task detail
 * response — not in `@roomate/core`'s `TaskDetail` yet, so read through this local shape and the
 * cast below until it lands, then delete both and use the real field (no runtime check guards
 * this shape matching the server's once #140 ships).
 */
type NoAgentFit = { code: "no_agent_fit"; message: string; areas: string[]; uncovered: string[]; suggestedSplit: { name: string; areas: string[] }[] };

/** Badge copy for a known approval issue. Anything else, including an inherited property name, stays off the page. */
function issueLabel(issue: string | null | undefined): string | undefined {
  return isApprovalIssue(issue) ? APPROVAL_ISSUE_LOG[issue] : undefined;
}

/** The checkout already contains this branch in main: it was merged outside the queue, or the queue has not recorded it yet. */
function branchAlreadyOnMain(t: TaskDTO, snap: GitSnapshotDTO | null | undefined): boolean {
  if (!t.branch || !snap) return false;
  return snap.branches.some((b) => b.merged && (b.name === t.branch || b.taskId === t.id));
}

/** Done-task headline. Merge state wins over a leftover approval issue. The waiting line is only for a branch the queue will still merge. */
function doneHeadline(t: TaskDTO, snap: GitSnapshotDTO | null | undefined): string {
  const issue = issueLabel(t.approvalIssue);
  if (t.mergeState === "merged") return "Shipped. Every check passed.";
  if (t.mergeState === "merge_failed") return "Merge failed.";
  if (branchAlreadyOnMain(t, snap)) return "Shipped.";
  const queued = mergeQueueHandles(t);
  // not_on_origin stays queued. A missing push, rewritten history, or a deleted branch all look the same from here.
  if (t.approvalIssue === "not_on_origin" && t.approvedSha && queued) return "Approved. That commit is not on origin.";
  // "Approved" names a commit. changed_after_approval clears approved_sha, so that word stays off until a sha is set.
  if (issue) return "Not merged.";
  if (queued) return "Done. Waiting to merge.";
  return "Shipped.";
}

const STAGE_TONE: Record<string, "neutral" | "live" | "signal" | "danger" | "done"> = {
  backlog: "neutral",
  ready: "neutral",
  in_progress: "live",
  blocked: "signal",
  verifying: "live",
  review: "signal",
  done: "done",
  cancelled: "neutral",
};

const PR_TONE: Record<string, "live" | "done" | "neutral"> = { open: "live", merged: "done", closed: "neutral" };

/** A node reports the PR url, so it only becomes an href when it is https; anything else renders as plain text. */
export function safePrUrl(url: string): string | null {
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

export function TaskPage() {
  const { id = "" } = useParams();
  const q = useTask(id);
  const project = useProject(q.data?.task.projectId ?? "");
  const actors = useActors().data;
  const runs = useRuns(`taskId=${id}`).data ?? [];
  const git = useGitSnapshot(q.data?.task.projectId ?? null);
  if (q.error) return <ErrorNote error={q.error} />;
  if (!q.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );
  const d = q.data;
  const t = d.task;
  const noAgentFit = (d as typeof d & { noAgentFit?: NoAgentFit | null }).noAgentFit ?? null;
  const park = parkNote(d);
  const approach = approachCard(d);
  const last = d.evidence.at(-1)?.result;
  const open = d.decisions.filter((x) => x.status === "open");
  const met = t.acceptanceCriteria.filter((c) => last?.acceptance.find((a) => a.criterionId === c.id)?.status === "met").length;
  const builder = actors?.find((a) => a.id === t.builderId);
  const cost = runs.reduce((s, r) => s + r.costUsd, 0);
  const live = runs.find((r) => r.status === "running" || r.status === "starting");
  const abandonment = abandonmentFor(t, d.timeline, actors ?? []);
  const headline =
    abandonment
      ? "This merge was abandoned."
      : t.stage === "done"
      ? doneHeadline(t, git.data)
      : open.length
        ? "Waiting on a person."
        : live
          ? "Being built right now."
          : t.stage === "review"
            ? "Built, in review."
            : t.builderId
              ? "Assigned, not started yet."
              : "Not picked up yet.";

  return (
    <Page wide>
      <PageHeader
        crumbs={[{ label: "Project", to: `/projects/${t.projectId}` }, ...(d.intent ? [{ label: d.intent.title, to: `/intents/${d.intent.id}` }] : []), { label: `#${t.number}` }]}
        title={t.title}
        status={
          <Pill tone={STAGE_TONE[t.stage]} testId="task-stage">
            {STAGE_LABEL[t.stage]}
          </Pill>
        }
        meta={<span className="text-ink-3">{t.priority} priority · size {t.size}</span>}
        actions={<TaskActions detail={d} tasks={project.data?.tasks} blastRadiusError={project.error} />}
      />
      <ProjectArchivedNotice projectId={t.projectId} />

      <Bento className="mt-8">
        <Tile span={3} className="flex items-center gap-6">
          <Ring value={met} total={t.acceptanceCriteria.length} tone={met === t.acceptanceCriteria.length && met > 0 ? "ok" : "ink"} />
          <div className="min-w-0">
            <p className="text-[19px] font-medium leading-snug tracking-[-0.01em]">{headline}</p>
            <p className="mt-1.5 text-[13.5px] text-ink-2">
              {t.acceptanceCriteria.length ? `${met} of ${t.acceptanceCriteria.length} acceptance criteria met` : "No acceptance criteria yet"}
              {last ? ` · last check ${last.verdict}` : ""}
            </p>
          </div>
        </Tile>

        <Tile span={2} title="Who">
          <div className="flex items-center gap-3">
            {builder ? <Avatar name={builder.name} kind={builder.kind} size={40} live={!!live} /> : <span className="h-10 w-10 rounded-full border-2 border-dashed border-rule" />}
            <div className="min-w-0">
              <p className="truncate text-[15px] font-medium">{builder?.name ?? "Nobody yet"}</p>
              <p className="text-[12.5px] text-ink-3">{builder ? (builder.kind === "agent" ? `agent · ${(builder.runtime ?? "claude_code").replace("_", " ")}` : builder.role.replace("_", " ")) : "builder"}</p>
            </div>
          </div>
          {t.reviewerIds.length > 0 && (
            <div className="mt-4 flex items-center gap-2">
              <div className="flex -space-x-2">
                {t.reviewerIds.map((r) => {
                  const a = actors?.find((x) => x.id === r);
                  return a ? <Avatar key={r} name={a.name} kind={a.kind} size={26} /> : null;
                })}
              </div>
              <span className="min-w-0 truncate text-[12.5px] text-ink-2">
                reviewed by{" "}
                {t.reviewerIds.map((r, i) => (
                  <span key={r}>
                    {i > 0 && ", "}
                    <ActorName id={r} actors={actors} />
                  </span>
                ))}
              </span>
            </div>
          )}
        </Tile>

        <Tile span={1} title="Risk">
          <p className={cx("text-[24px] font-semibold capitalize tracking-[-0.03em]", t.risk.tier === "low" ? "text-[color:var(--ok)]" : t.risk.tier === "medium" ? "text-[color:var(--medium)]" : "text-high")}>{t.risk.tier}</p>
          <Disclosure summary="why" className="mt-2">
            <ul className="space-y-1 text-[12.5px] text-ink-2">
              {t.risk.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </Disclosure>
        </Tile>

        {approach && <ApproachTile approach={approach} actors={actors} />}

        {open.length > 0 && (
          <Tile span={6} className="!bg-signal-soft/70">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[13px] text-ink-2">Waiting on a person</p>
                {open.map((dec) => (
                  <div key={dec.id} className="mt-1.5 text-[17px] leading-snug">
                    <div className="mb-0.5">
                      <DecisionCrumb decision={dec} />
                    </div>
                    {dec.question} <span className="text-[14px] text-ink-2">· for <ActorName id={dec.assigneeId} actors={actors} /></span>
                  </div>
                ))}
              </div>
              <Link to="/" className="shrink-0 rounded-full bg-ink px-5 py-2.5 text-[14px] text-paper">
                Answer on Home
              </Link>
            </div>
          </Tile>
        )}

        <Tile span={4} title="Done when" aside={last && last.verdict !== "pass" ? <span className="text-high">last check {last.verdict}</span> : undefined}>
          {t.acceptanceCriteria.length === 0 ? (
            <p className="text-[14px] text-ink-3">No acceptance criteria yet, so this task can't be routed.</p>
          ) : (
            <ul className="divide-y divide-rule/60">
              {t.acceptanceCriteria.map((c) => {
                const v = last?.acceptance.find((a) => a.criterionId === c.id);
                return (
                  <Check key={c.id} state={v ? (v.status === "met" ? "met" : v.status === "not_met" ? "not_met" : "unknown") : "pending"} detail={v?.evidence || undefined}>
                    {c.text}
                  </Check>
                );
              })}
            </ul>
          )}
          {last && last.verdict !== "pass" && (
            <Disclosure summary="What the last check found" className="mt-3" defaultOpen>
              <ul className="list-disc space-y-1 pl-5 text-[13.5px] text-ink-2">
                {last.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </Disclosure>
          )}
        </Tile>

        {noAgentFit && <NoAgentFitNote fit={noAgentFit} />}

        {park && <ParkNoteTile note={park} />}

        <Routing detail={d} actors={actors} />

        {t.description && (
          <Tile span={4} title="The brief">
            <Clamp>
              <Markdown text={t.description} className="text-[14.5px] leading-relaxed" />
            </Clamp>
          </Tile>
        )}

        {abandonment && (
          <Tile span={6} className="!bg-signal-soft/70" testId="abandonment">
            <AbandonmentLine abandonment={abandonment} />
          </Tile>
        )}

        <TaskCode task={t} abandonment={abandonment} />

        <TaskChanges taskId={t.id} />

        {runs.length > 0 && <RunsTile runs={runs} actors={actors} cost={cost} />}
        <Story detail={d} actors={actors} wide={runs.length === 0} />
      </Bento>
    </Page>
  );
}

export function TaskCode({ task: t, abandonment }: { task: TaskDTO; abandonment: AbandonmentView | null }) {
  const approvalLabel = issueLabel(t.approvalIssue);
  return (
        <Tile span={2} title="Code">
          {t.branch ? <p className="truncate font-mono text-[12.5px]">{t.branch.replace(/^rm\//, "")}</p> : <p className="text-[13px] text-ink-3">No branch yet</p>}
          {t.pullRequest && (
            <p className="mt-2 flex items-center gap-2 text-[13px]" data-testid="task-pr">
              {safePrUrl(t.pullRequest.url) ? (
                <a href={safePrUrl(t.pullRequest.url)!} target="_blank" rel="noreferrer" className="underline decoration-rule hover:text-ink" data-testid="task-pr-link">
                  PR #{t.pullRequest.number} ↗
                </a>
              ) : (
                <span data-testid="task-pr-link">PR #{t.pullRequest.number}</span>
              )}
              <Pill tone={PR_TONE[t.pullRequest.state] ?? "neutral"} testId="task-pr-state">{t.pullRequest.state}</Pill>
            </p>
          )}
          {t.mergeState && (
            <p className="mt-2">
              {t.mergeState === "abandoned" ? <AbandonedBadge abandonment={abandonment} /> : <Pill tone={t.mergeState === "merged" ? "live" : "danger"}>{t.mergeState === "merged" ? "merged to main" : "merge failed"}</Pill>}
            </p>
          )}
          {t.mergeRework && (
            <p className="mt-2 text-[12.5px] text-ink-2" data-testid="merge-rework">
              <Pill tone="signal">merge rework {t.mergeRework.attempt}</Pill> conflict with main{t.mergeRework.files.length ? ` in ${t.mergeRework.files.join(", ")}` : ""}; back with the builder to merge main in and resubmit
            </p>
          )}
          {t.mergeOutput && (
            <Disclosure summary="Last merge output" className="mt-3">
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11.5px] text-ink-2" data-testid="merge-output">{t.mergeOutput}</pre>
            </Disclosure>
          )}
          {(t.approvedSha || t.originTipSha) && (
            <dl className="mt-3 space-y-0.5 text-[12px]" data-testid="sha-binding">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Approved</dt>
                <dd className="font-mono">{t.approvedSha ? t.approvedSha.slice(0, 7) : "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-3">Origin tip</dt>
                <dd className="font-mono">{t.originTipSha ? t.originTipSha.slice(0, 7) : "—"}</dd>
              </div>
            </dl>
          )}
          {approvalLabel && (
            <p className="mt-2">
              <Pill tone="danger" testId="approval-issue">
                {approvalLabel}
              </Pill>
            </p>
          )}
          {t.areas.length > 0 && (
            <Disclosure summary={`${t.areas.length} area${t.areas.length === 1 ? "" : "s"}`} className="mt-3">
              <ul className="space-y-0.5 font-mono text-[11.5px] text-ink-2">
                {t.areas.map((a) => (
                  <li key={a} className="truncate">
                    {a}
                  </li>
                ))}
              </ul>
            </Disclosure>
          )}
        </Tile>
  );
}

/** The approach the builder took, shown before the checks on medium+ tasks (decision gate). */
function ApproachTile({ approach, actors }: { approach: ApproachCard; actors: ActorDTO[] | undefined }) {
  const name = useNames(actors);
  return (
    <Tile
      span={6}
      title="Approach"
      testId="approach"
      aside={approach.status === "after_code" ? <Pill tone="signal" testId="approach-after-code">approach recorded after code</Pill> : undefined}
    >
      {approach.decisions.length === 0 ? (
        <p className="text-[13.5px] text-ink-3">No approach recorded yet</p>
      ) : (
        <ol className="space-y-2.5">
          {latestPerSummary(approach.decisions).map((p) => (
            <li key={p.id} className="rounded-2xl bg-ink/[0.03] px-4 py-3" data-testid="approach-decision">
              <p className="text-[14px] font-medium leading-snug">{p.summary}</p>
              <p className="mt-1 line-clamp-4 break-words text-[12.5px] leading-relaxed text-ink-2" title={p.rationale}>{p.rationale}</p>
              {p.alternatives.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[12.5px] text-ink-3">
                  {p.alternatives.map((a, i) => (
                    <li key={i}>
                      <span className="text-ink-2">{a.option}</span> · {a.whyNot}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1.5 text-[11.5px] text-ink-3">
                {name(p.actorId)} · {formatAgo(Date.now() - p.createdAt)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Tile>
  );
}

/** Resubmissions record the same approach again; show only the newest entry for each summary, newest last. */
export function latestPerSummary<T extends { summary: string; createdAt: number }>(decisions: readonly T[]): T[] {
  const byText = new Map<string, T>();
  for (const d of [...decisions].sort((a, b) => a.createdAt - b.createdAt)) {
    byText.delete(d.summary);
    byText.set(d.summary, d);
  }
  return [...byText.values()];
}

/** Long descriptions start folded to a few lines. */
function Clamp({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className={cx("relative overflow-hidden", !open && "max-h-[7.5em]")}>
        {children}
        {!open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[color:var(--card)] to-transparent" />}
      </div>
      <button onClick={() => setOpen((o) => !o)} className="mt-2 text-[13px] text-ink-2 hover:text-ink">
        {open ? "Show less" : "Read more"}
      </button>
    </div>
  );
}

export function AbandonMergeButton({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return visible ? <Action danger onClick={onClick}>Abandon merge</Action> : null;
}

export function AbandonMergeForm({
  reason,
  dependents,
  decisions,
  pending,
  error,
  blastRadiusReady,
  blastRadiusError,
  onReasonChange,
  onCancel,
  onSubmit,
}: {
  reason: string;
  dependents: readonly AffectedDependent[];
  decisions: TaskDetail["decisions"];
  pending: boolean;
  error: unknown;
  blastRadiusReady: boolean;
  blastRadiusError: unknown;
  onReasonChange: (reason: string) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <p className="text-[13.5px] leading-relaxed text-ink-2">This cancels the task without deleting its branch. The reason is kept in the task history.</p>
      <Field label="Reason" hint="at least 10 characters">
        <AbandonReasonInput value={reason} onChange={onReasonChange} />
      </Field>
      <p id="abandon-reason-hint" className="text-[12px] text-ink-3">Explain why this branch should never merge.</p>
      {blastRadiusReady ? (
        <AbandonBlastRadius dependents={dependents} decisions={decisions} />
      ) : blastRadiusError ? (
        <div>
          <p className="text-[13px] text-high">The blast radius could not be loaded. Try again before abandoning this merge.</p>
          <ErrorNote error={blastRadiusError} />
        </div>
      ) : (
        <p className="text-[13px] text-ink-3" data-testid="abandon-blast-radius-loading">Loading dependents and open decisions…</p>
      )}
      <ErrorNote error={error} />
      <div className="flex justify-end gap-2">
        <Action onClick={onCancel}>Keep merge</Action>
        <Action danger type="submit" disabled={!blastRadiusReady || reason.trim().length < 10 || pending}>Abandon merge</Action>
      </div>
    </form>
  );
}

function TaskActions({ detail, tasks, blastRadiusError }: { detail: TaskDetail; tasks: readonly TaskDTO[] | undefined; blastRadiusError: unknown }) {
  const me = useMe().data!;
  const t = detail.task;
  const transition = useTransition();
  const claim = useClaim();
  const archived = useProjectArchived(t.projectId);
  const abandon = useAbandonMerge();
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  if (me.access === "viewer") return null;
  const can = (to: string) => {
    if (t.stage === "blocked") return to === (t.blockedFrom ?? "ready") || to === "cancelled";
    const map: Record<string, string[]> = {
      backlog: ["ready", "cancelled"],
      ready: ["backlog", "blocked", "cancelled"],
      in_progress: ["ready", "blocked", "cancelled"],
      review: ["in_progress", "cancelled"],
      done: [],
      cancelled: ["backlog"],
      verifying: [],
    };
    return map[t.stage]?.includes(to) ?? false;
  };
  const go = (to: Parameters<typeof transition.mutate>[0]["to"]) => transition.mutate({ id: t.id, to });
  const submitAbandon = (event: FormEvent) => {
    event.preventDefault();
    if (!tasks || reason.trim().length < 10) return;
    abandon.mutate({ id: t.id, reason: reason.trim() }, { onSuccess: () => setAbandonOpen(false) });
  };
  const dependents = tasks ? dependentsForAbandon(t, tasks) : [];
  const decisions = detail.decisions.filter((decision) => decision.status === "open");
  return (
    <>
      {t.stage === "ready" && !archived && (!t.builderId || t.builderId === me.id) && (
        <Action primary onClick={() => claim.mutate(t.id, { onSuccess: (r) => setMsg(r.status === "claimed" ? null : `${r.status}: ${r.reason}`) })}>
          I'll build this
        </Action>
      )}
      {can("ready") && t.stage === "backlog" && <Action onClick={() => go("ready")}>Mark ready</Action>}
      {t.stage === "blocked" && <Action onClick={() => go(t.blockedFrom ?? "ready")}>Unblock</Action>}
      {can("blocked") && <Action onClick={() => go("blocked")}>Block</Action>}
      {can("ready") && t.stage === "in_progress" && <Action onClick={() => go("ready")}>Release</Action>}
      {can("cancelled") && (
        <Action danger onClick={() => go("cancelled")}>
          Cancel
        </Action>
      )}
      <AbandonMergeButton visible={canAbandonMerge(me, t)} onClick={() => setAbandonOpen(true)} />
      {msg && <span className="text-[12.5px] text-high">{msg}</span>}
      <ErrorNote error={transition.error ?? claim.error ?? abandon.error} />
      {abandonOpen && (
        <Dialog title="Abandon merge" onClose={() => setAbandonOpen(false)}>
          <AbandonMergeForm
            reason={reason}
            dependents={dependents}
            decisions={decisions}
            pending={abandon.isPending}
            error={abandon.error}
            blastRadiusReady={!!tasks}
            blastRadiusError={blastRadiusError}
            onReasonChange={setReason}
            onCancel={() => setAbandonOpen(false)}
            onSubmit={submitAbandon}
          />
        </Dialog>
      )}
    </>
  );
}

/** Why a task is parked, with the split task or split-into tasks linked. Sits outside any disclosure, above routing. */
function ParkNoteTile({ note }: { note: ParkNote }) {
  return (
    <Tile span={6} className="!bg-signal-soft/70" testId="park-note">
      <p className="text-[16px] leading-snug">
        {note.parts.map((p, i) =>
          typeof p === "string" ? (
            <span key={i}>{p}</span>
          ) : (
            <Link key={i} to={`/tasks/${p.id}`} className="font-medium underline underline-offset-2" data-testid="park-note-link">
              #{p.number}
            </Link>
          ),
        )}
      </p>
    </Tile>
  );
}

/** Nobody may build this: the one line a person shouldn't have to click a disclosure for. */
function NoAgentFitNote({ fit }: { fit: NoAgentFit }) {
  return (
    <Tile span={6} className="!bg-signal-soft/70" testId="no-agent-fit">
      <p className="text-[13px] text-ink-2">Nobody may build this</p>
      <p className="mt-1.5 text-[16px] leading-snug">{fit.message}</p>
      <dl className="mt-3 space-y-1 text-[12.5px] text-ink-2">
        <div className="flex gap-x-2">
          <dt className="shrink-0 font-medium text-ink">Areas</dt>
          <dd className="min-w-0 flex-1 truncate font-mono">{fit.areas.join(", ")}</dd>
        </div>
        {fit.suggestedSplit.map((s) => (
          <div key={s.name} className="flex gap-x-2">
            <dt className="shrink-0 font-medium text-ink">{s.name} covers</dt>
            <dd className="min-w-0 flex-1 truncate font-mono">{s.areas.join(", ")}</dd>
          </div>
        ))}
        {fit.uncovered.length > 0 && (
          <div className="flex gap-x-2">
            <dt className="shrink-0 font-medium text-ink">Nobody covers</dt>
            <dd className="min-w-0 flex-1 truncate font-mono">{fit.uncovered.join(", ")}</dd>
          </div>
        )}
      </dl>
    </Tile>
  );
}

/** Routing as one card: who was chosen and how sure, with the reasoning and overrides folded away. */
function Routing({ detail, actors }: { detail: TaskDetail; actors: ActorDTO[] | undefined }) {
  const t = detail.task;
  const s: RouteSuggestion | null = t.suggestion;
  const assign = useAssign();
  const route = useRoute();
  const me = useMe().data!;
  const [builderId, setBuilderId] = useState<string>(s?.builderId ?? "");
  const [reviewerId, setReviewerId] = useState<string>(s?.reviewerIds[0] ?? "");
  const [reason, setReason] = useState("");
  const archived = useProjectArchived(t.projectId);
  const editable = !archived && (t.stage === "ready" || t.stage === "backlog") && me.kind === "human" && me.access !== "viewer";
  const accepted = s && t.builderId === s.builderId && t.mode === s.mode;

  if (!s) {
    return (
      <Tile span={2} title="Routing">
        <p className="text-[13.5px] text-ink-3">{t.acceptanceCriteria.length ? "Not routed yet." : "Routing starts once the task has acceptance criteria."}</p>
        {editable && t.acceptanceCriteria.length > 0 && (
          <div className="mt-3">
            <Action onClick={() => route.mutate(t.id)}>Route now</Action>
          </div>
        )}
      </Tile>
    );
  }
  const chosen = actors?.find((a) => a.id === s.builderId);
  const builder = actors?.find((a) => a.id === builderId);
  const mode = builder?.kind === "human" ? "human" : reviewerId ? "agent_with_review" : "agent";
  const chosenScore = s.builders.find((b) => b.actorId === s.builderId);
  const chosenRecord = chosenScore ? visibleTrack(chosenScore) : null;

  return (
    <Tile span={2} title="Routing" aside={`${Math.round(s.confidence * 100)}% sure`}>
      <div className="flex items-center gap-3">
        {chosen ? <Avatar name={chosen.name} kind={chosen.kind} size={36} /> : null}
        <div className="min-w-0">
          <p className="truncate text-[15px] font-medium">{chosen?.name ?? "No eligible builder"}</p>
          <p className="text-[12.5px] text-ink-3">{s.mode.replaceAll("_", " ")}</p>
        </div>
      </div>
      {editable && !accepted && (
        <div className="mt-4">
          <Action primary disabled={!s.builderId || assign.isPending} onClick={() => assign.mutate({ id: t.id, mode: s.mode, builderId: s.builderId, reviewerIds: s.reviewerIds })}>
            Accept suggestion
          </Action>
        </div>
      )}
      {accepted && <p className="mt-3 text-[12.5px] text-[color:var(--ok)]">Assigned as suggested</p>}
      <Disclosure summary="Why this builder" className="mt-4">
        <p className="text-[13px] leading-relaxed text-ink-2" data-testid="routing-explanation">
          {s.explanation}
        </p>
        <ul className="mt-3 space-y-1.5">
          {s.builders.slice(0, 5).map((b) => {
            const track = visibleTrack(b);
            return (
              <li key={b.actorId} className={cx("flex items-center gap-2 text-[12.5px]", track && "flex-wrap")}>
                <span className={cx("w-24 truncate", b.actorId === s.builderId && "font-medium")}>
                  <ActorName id={b.actorId} actors={actors} />
                </span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/[0.06]">
                  <span className={cx("block h-full rounded-full", b.actorId === s.builderId ? "bg-ink" : "bg-ink-3/50")} style={{ width: `${Math.round(b.score * 100)}%` }} />
                </span>
                <span className="w-9 text-right tabular-nums text-ink-3">{b.score.toFixed(2)}</span>
                {track && (
                  <span className="basis-full pl-[6.5rem] text-[11.5px] text-ink-3" data-testid="candidate-track">
                    {trackLine(track)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {chosenRecord && chosenRecord.byModel.length > 0 && (
          <p className="mt-3 text-[12.5px] text-ink-3" data-testid="routing-by-model">
            by model · {byModelLine(chosenRecord)}
          </p>
        )}
        {s.disqualified.length > 0 && (
          <Disclosure summary={`${s.disqualified.length} not eligible`} className="mt-3">
            <ul className="space-y-0.5 text-[12px] text-ink-3">
              {s.disqualified.map((x) => (
                <li key={x.actorId}>
                  {x.name}: {x.reason}
                </li>
              ))}
            </ul>
          </Disclosure>
        )}
      </Disclosure>
      {editable && (
        <Disclosure summary="Override" className="mt-2">
          <div className="space-y-2">
            <select className={inputCls} value={builderId} onChange={(e) => setBuilderId(e.target.value)} aria-label="Builder">
              <option value="">Builder…</option>
              {builderOptions(actors ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
            <select className={inputCls} value={reviewerId} onChange={(e) => setReviewerId(e.target.value)} aria-label="Reviewer">
              <option value="">No human reviewer</option>
              {reviewerOptions(actors ?? [], builderId).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
            <input className={inputCls} placeholder="Why? (the router learns from it)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex gap-2">
              <Action primary disabled={!builderId || assign.isPending} onClick={() => assign.mutate({ id: t.id, mode, builderId, reviewerIds: reviewerId ? [reviewerId] : [], reason })}>
                Assign
              </Action>
              <Action onClick={() => route.mutate(t.id)}>Re-route</Action>
            </div>
          </div>
        </Disclosure>
      )}
      <ErrorNote error={assign.error ?? route.error} />
    </Tile>
  );
}

/** Runs as a card: the latest one up front, the rest folded. */
function RunsTile({ runs, actors, cost }: { runs: RunDTO[]; actors: ActorDTO[] | undefined; cost: number }) {
  const [latest, ...earlier] = runs;
  const row = (r: RunDTO, big = false) => {
    const active = r.status === "running" || r.status === "starting";
    const agent = actors?.find((a) => a.id === r.agentId);
    return (
      <Link key={r.id} to={`/runs/${r.id}`} className={cx("flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-ink/[0.04]", big && "bg-ink/[0.03]")} data-testid="run-row">
        {agent && <Avatar name={agent.name} kind="agent" size={big ? 34 : 26} live={active} />}
        <span className="min-w-0 flex-1">
          <span className={cx("flex min-w-0 items-center gap-2", big ? "text-[14.5px] font-medium" : "text-[13px]")}>
            <span className="truncate">{RUN_STATUS_LABEL[r.status]}</span>
            <span className="shrink-0">
              <RunRuntime run={r} />
            </span>
          </span>
          <span className="block text-[12px] text-ink-3">{active ? `started ${formatAgo(Date.now() - r.startedAt)} ago` : `${formatMin(((r.finishedAt ?? r.startedAt) - r.startedAt) / 60_000)} · ${r.numTurns} turns`}</span>
        </span>
        <span className="shrink-0 text-[12.5px] tabular-nums text-ink-2">{money(r.costUsd)}</span>
      </Link>
    );
  };
  return (
    <Tile span={3} title="Runs" aside={`${runs.length} · ${money(cost)}`}>
      {latest && row(latest, true)}
      {earlier.length > 0 && (
        <Disclosure summary={`${earlier.length} earlier run${earlier.length === 1 ? "" : "s"}`} className="mt-2">
          <div className="space-y-0.5">{earlier.map((r) => row(r))}</div>
        </Disclosure>
      )}
    </Tile>
  );
}

type Item = { ts: number; text: string; key: string; chief?: boolean };

/** The story: decisions the agents recorded, as cards; the raw event log folded underneath. */
function Story({ detail, actors, wide }: { detail: TaskDetail; actors: ActorDTO[] | undefined; wide: boolean }) {
  const name = useNames(actors);
  const threads = useThreads({ taskId: detail.task.id }).data ?? [];
  const messages = useMessages({ taskId: detail.task.id }).data ?? [];
  const decisions: ProvenanceDTO[] = [...detail.provenance].sort((a, b) => a.createdAt - b.createdAt);
  // The Chief's acts carry their reason in the audit feed, not in the task's events, so they join the log here (chief-of-office.md §8.3).
  const chiefActions = useChiefActions({ projectId: detail.task.projectId, limit: 500 }).data;
  const events: Item[] = [
    ...detail.timeline.filter((e) => e.type !== "provenance.recorded").map((e): Item => ({ ts: e.ts, text: describeEvent(e, name), key: `e${e.seq}` })),
    ...chiefActionsForTask(chiefActions ?? [], detail.task.id).map((a): Item => ({ ts: a.at, text: chiefTimelineLine(a), key: a.id, chief: true })),
  ].sort((x, y) => x.ts - y.ts);
  return (
    <Tile span={wide ? 6 : 3} title="How it was decided" aside={`${decisions.length} decision${decisions.length === 1 ? "" : "s"}`}>
      {decisions.length === 0 ? (
        <p className="text-[13.5px] text-ink-3">No recorded decisions yet.</p>
      ) : (
        <ol className="space-y-2.5">
          {decisions.slice(-3).map((p) => (
            <li key={p.id} className="rounded-2xl bg-ink/[0.03] px-4 py-3" data-testid="provenance">
              <p className="text-[14px] font-medium leading-snug">{p.summary}</p>
              <p className="mt-1 line-clamp-2 text-[12.5px] leading-relaxed text-ink-2">{p.rationale}</p>
              <p className="mt-1.5 text-[11.5px] text-ink-3">
                {historyName(actors, p.actorId)} · {formatAgo(Date.now() - p.createdAt)} ago{p.basedOn.length ? ` · ${p.basedOn.length} source${p.basedOn.length === 1 ? "" : "s"}` : ""}
              </p>
            </li>
          ))}
        </ol>
      )}
      {decisions.length > 3 && (
        <Disclosure summary={`${decisions.length - 3} earlier decisions`} className="mt-3">
          <ol className="space-y-2">
            {decisions.slice(0, -3).map((p) => (
              <li key={p.id} className="text-[13px]" data-testid="provenance">
                <span className="font-medium">{p.summary}</span> <span className="text-ink-3">· {historyName(actors, p.actorId)}</span>
              </li>
            ))}
          </ol>
        </Disclosure>
      )}
      <Disclosure summary={`Full log · ${events.length} events`} className="mt-3">
        <ol className="space-y-1.5">
          {events.map((it) => (
            <li key={it.key} className="flex gap-3 text-[12.5px]" data-testid={it.chief ? "chief-timeline-row" : undefined}>
              <span className="w-12 shrink-0 tabular-nums text-ink-3">{new Date(it.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
              <span className="min-w-0 text-ink-2">{it.text}</span>
            </li>
          ))}
        </ol>
      </Disclosure>
      {threads.length > 0 && <TaskThreads threads={threads} messages={messages} actors={actors ?? []} />}
    </Tile>
  );
}

function TaskThreads({ threads, messages, actors }: { threads: ThreadDTO[]; messages: MessageDTO[]; actors: ActorDTO[] }) {
  const actorName = (id: string) => historyName(actors, id);
  return (
    <div className="mt-5 border-t border-rule/60 pt-4" data-testid="task-threads">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-medium text-ink-2">Conversations</h3>
        <span className="text-[12px] text-ink-3">{threads.length} thread{threads.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2">
        {threads.map((thread) => {
          const inThread = messages.filter((message) => message.threadId === thread.threadId).reverse();
          return (
            <Disclosure
              key={thread.threadId}
              summary={`${thread.participants.map(actorName).join(" ↔ ")} · ${thread.messageCount} message${thread.messageCount === 1 ? "" : "s"}`}
              className="rounded-2xl bg-ink/[0.03] px-4 py-3"
              testId="task-thread"
            >
              <ol className="space-y-3">
                {inThread.map((message) => (
                  <li key={message.id} className="text-[13px]" data-testid="task-thread-message">
                    <p className="flex flex-wrap items-baseline gap-2">
                      <span className="font-medium">{actorName(message.fromActor)}</span>
                      <span className="text-[11.5px] text-ink-3">{formatAgo(Date.now() - message.createdAt)} ago</span>
                    </p>
                    <div className="mt-1 leading-relaxed text-ink-2"><Markdown text={message.body} /></div>
                  </li>
                ))}
              </ol>
              <Link to={`/conversations`} className="mt-3 inline-block text-[12px] text-ink-2 underline decoration-rule hover:text-ink">Open in Conversations</Link>
            </Disclosure>
          );
        })}
      </div>
    </div>
  );
}
